import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { WarehouseList } from '../../../components/WarehouseList';
import { ROUTES } from '../../../constants/routes';
import { useAddress } from '../../../hooks/useAddress';
import { useAuth } from '../../../hooks/useAuth';
import { Loader } from '../../../components/ui/Loader';
import { useModal } from '../../../hooks/useModal';
import AddWarehousePage from './add';

export const SettingWarehouse: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading, refetch } = useAddress(user?.id as string);
  const { open, close, Modal } = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredWarehouses = React.useMemo(() => {
    const list = data?.pickupAddresses ?? [];
    
    // Sort so primary address (default_status === 1) is always on top
    const sortedList = [...list].sort((a, b) => {
      if (a.default_status === 1) return -1;
      if (b.default_status === 1) return 1;
      return 0;
    });

    if (!searchTerm) return sortedList;
    const lowerSearch = searchTerm.toLowerCase();
    return sortedList.filter(
      (w) =>
        w.company_name?.toLowerCase().includes(lowerSearch) ||
        w.address1?.toLowerCase().includes(lowerSearch) ||
        w.contact_name?.toLowerCase().includes(lowerSearch)
    );
  }, [data, searchTerm]);

  return (
    <div className="py-4 lg:py-8">
      {/* Header */}
      <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              Manage Warehouse
            </h1>
          </div>
          {/* <div className="flex items-center gap-4 text-gray-600">
            <span className="font-medium">
              {user?.firstName} {user?.lastName}, {user?.email}
            </span>
            <Button variant="link" className="text-primary p-0 h-auto">
              Go to profile
            </Button>
          </div> */}
        </div>

        <div className="flex items-center gap-4">
          <Button
            onClick={() =>
              open({
                Component: () => (
                  <AddWarehousePage
                    onSuccess={() => {
                      refetch();
                      close();
                    }}
                  />
                ),
                centered: true,
                closable: true,
                className: 'max-w-4xl w-full h-[92vh]',
              })
            }
            className="w-fit whitespace-nowrap"
          >
            + Add Warehouse
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex gap-4 items-center -mb-4">
          <Input
            placeholder="Search warehouse..."
            className="w-full lg:w-80"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
          />
          {searchTerm && (
            <Button
              variant="outline"
              onClick={() => setSearchTerm('')}
              className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              Reset Filters
            </Button>
          )}
        </div>
        <div className="bg-card rounded-[16px] shadow-sm border">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-10 py-4 border-b text-lg font-bold text-gray-600">
            <div className="col-span-6">Address Nickname</div>
            <div className="col-span-2"></div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
        </div>

        {/* Card */}
        <div className="bg-card rounded-[16px] shadow-sm border">
          {isLoading ? (
            <Loader text="Loading warehouses" />
          ) : (
            <WarehouseList warehouses={filteredWarehouses} />
          )}
        </div>
      </div>
      <Modal />
    </div>
  );
};

export default SettingWarehouse;
